import { useState, useEffect } from "react"
import { useNavigate, Link, useLocation } from "react-router-dom"
import { Eye, EyeOff, AlertCircle, Loader2, Shield, Users, Zap } from "lucide-react"
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth"
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../services/firebase"
import { useAuth } from "../contexts/AuthProvider"
import InvitationService from "../services/InvitationService"
import { InvitationUtils } from "../utils/invitationUtils"
import { RedirectUtils } from "../utils/redirectUtils"
import LOGO from "../assets/logo_h.svg"

const FF = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

const T = {
  radius:       8,
  bg:           "#fff",
  text:         "#111827",
  textMuted:    "#374151",
  textTertiary: "#6b7280",
}

const features = [
  { icon: Shield, label: "Verified Opportunities"  },
  { icon: Users,  label: "Top Employers"           },
  { icon: Zap,    label: "Fast-Track Applications" },
]

// ── useIsMobile ───────────────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener("resize", handler)
    return () => window.removeEventListener("resize", handler)
  }, [])
  return isMobile
}

// ── Field ─────────────────────────────────────────────────────────────────────
const Field = ({ label, ...props }) => (
  <div>
    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.textMuted, fontFamily: FF, marginBottom: 7 }}>
      {label}
    </label>
    <input
      style={{
        width: "100%", height: 42, padding: "0 14px",
        border: "1px solid #d1d5db", borderRadius: T.radius,
        fontSize: 14, fontWeight: 500, color: T.text,
        background: T.bg, fontFamily: FF, outline: "none",
        transition: "border-color 0.12s", boxSizing: "border-box",
      }}
      onFocus={(e) => { e.target.style.borderColor = "#111827" }}
      onBlur={(e)  => { e.target.style.borderColor = "#d1d5db" }}
      {...props}
    />
  </div>
)

// ── PrimaryBtn ────────────────────────────────────────────────────────────────
const PrimaryBtn = ({ children, loading, ...props }) => (
  <button
    {...props}
    disabled={loading || props.disabled}
    style={{
      width: "100%", height: 42,
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
      border: "none", borderRadius: T.radius,
      background: loading || props.disabled ? "#9ca3af" : "#111827",
      color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: FF,
      cursor: loading || props.disabled ? "not-allowed" : "pointer",
      outline: "none", transition: "background 0.12s",
    }}
    onMouseEnter={(e) => { if (!loading && !props.disabled) e.currentTarget.style.background = "#27272a" }}
    onMouseLeave={(e) => { if (!loading && !props.disabled) e.currentTarget.style.background = "#111827" }}
  >
    {loading ? <Loader2 size={16} style={{ animation: "login-spin 0.8s linear infinite" }} /> : children}
  </button>
)

// ── GhostBtn ──────────────────────────────────────────────────────────────────
const GhostBtn = ({ children, onClick, disabled, style }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      height: 42, padding: "0 16px",
      display: "inline-flex", alignItems: "center", gap: 8,
      border: "1px solid #d1d5db", borderRadius: T.radius,
      background: T.bg, color: T.textMuted,
      fontSize: 14, fontWeight: 500, fontFamily: FF,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      outline: "none", transition: "border-color 0.12s",
      ...style,
    }}
    onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.borderColor = "#9ca3af" }}
    onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.borderColor = "#d1d5db" }}
  >
    {children}
  </button>
)

// ── LoginPage ─────────────────────────────────────────────────────────────────
const LoginPage = () => {
  const [email, setEmail]               = useState("")
  const [password, setPassword]         = useState("")
  const [error, setError]               = useState("")
  const [loading, setLoading]           = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [invitationInfo, setInvitationInfo] = useState(null)

  const navigate        = useNavigate()
  const location        = useLocation()
  const { refreshAuth } = useAuth()
  const isMobile        = useIsMobile()

  useEffect(() => {
    const storedInvitation = InvitationUtils.getStoredInvitation()
    if (storedInvitation) {
      setEmail(storedInvitation.email)
      setInvitationInfo(storedInvitation)
      return
    }
    const params           = new URLSearchParams(location.search)
    const invitedEmail     = params.get("invitedEmail")
    const invitationCode   = params.get("code")
    if (invitedEmail) {
      setEmail(invitedEmail)
      if (invitationCode) setInvitationInfo({ email: invitedEmail, code: invitationCode })
    }
  }, [location])

  // ── Google sign-in ────────────────────────────────────────────────────────
  const handleGoogleSignIn = async () => {
    if (invitationInfo) {
      setError(`Please sign in with ${invitationInfo.email} to accept this invitation.`)
      return
    }
    setGoogleLoading(true); setError("")
    try {
      const auth     = getAuth()
      const provider = new GoogleAuthProvider()
      const result   = await signInWithPopup(auth, provider)
      const user     = result.user

      const userRef  = doc(db, "users", user.uid)
      const userSnap = await getDoc(userRef)

      if (!userSnap.exists()) {
        await auth.signOut()
        setError("No account found for this Google account. Please sign up first.")
        return
      }

      await setDoc(userRef, { lastLogin: serverTimestamp() }, { merge: true })
      if (refreshAuth) await refreshAuth()
      await new Promise((r) => setTimeout(r, 300))
      RedirectUtils.redirectToStoredUrl(navigate, "/profile")
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user") setError("Google sign-in failed. Please try again.")
    } finally { setGoogleLoading(false) }
  }

  // ── Email/password sign-in ────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault(); setError("")
    if (!email || !email.includes("@")) { setError("Please enter a valid email address"); return }
    if (!password)                       { setError("Please enter your password"); return }
    setLoading(true)
    try {
      const auth           = getAuth()
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      const user           = userCredential.user

      if (invitationInfo?.code || invitationInfo?.id) {
        try {
          const stored = InvitationUtils.getStoredInvitation()
          if (stored) { await InvitationService.acceptInvitation(stored.id, user.uid); InvitationUtils.clearInvitation() }
        } catch (invErr) {
          console.error("Error processing invitation:", invErr)
          setError("Login successful, but there was an issue processing your invitation.")
          setLoading(false); return
        }
      }

      if (refreshAuth) await refreshAuth()
      await new Promise((r) => setTimeout(r, 300))
      RedirectUtils.redirectToStoredUrl(navigate, "/profile")
    } catch (err) {
      const msgs = {
        "auth/invalid-email":       "Invalid email address format",
        "auth/user-disabled":       "This account has been disabled",
        "auth/user-not-found":      "No account found with this email",
        "auth/wrong-password":      "Incorrect password",
        "auth/invalid-credential":  "Incorrect email or password",
        "auth/too-many-requests":   "Too many failed attempts. Please try again later.",
      }
      setError(msgs[err.code] || err.message)
    } finally { setLoading(false) }
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", background: T.bg, fontFamily: FF }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        @keyframes login-spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        input:disabled { opacity: 0.6; cursor: not-allowed; }
      `}</style>

      {/* ── LEFT PANEL — hidden on mobile ── */}
      {!isMobile && (
        <div style={{
          width: "35%", flexShrink: 0, minHeight: "100vh",
          background: "rgb(249,249,247)",
          display: "flex", flexDirection: "column", justifyContent: "space-between",
          padding: "44px 48px",
        }}>
          <div>
            {/* Logo */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 64 }}>
              <div style={{ width: 30, height: 30, borderRadius: T.radius, background: "#111827", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <img className="w-4 h-4 brightness-0 invert" src={LOGO} alt="Logo" />
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: FF, letterSpacing: "0.04em" }}>Resolut</span>
            </div>

            <p style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textTertiary, fontFamily: FF, margin: "0 0 14px" }}>
              Sector 01 · Welcome Back
            </p>

            <h2 style={{ fontSize: 34, fontWeight: 700, lineHeight: 1.15, color: T.text, margin: "0 0 16px", fontFamily: FF, letterSpacing: "-0.025em" }}>
              Pick Up Where<br />You Left Off
            </h2>

            <p style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.7, color: T.textTertiary, margin: "0 0 44px", fontFamily: FF }}>
              Sign in to continue your journey.
            </p>
          </div>

          <div style={{ display: "flex", gap: 20 }}>
            <a href="/terms" style={{ fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textDecoration: "none" }}
              onMouseEnter={(e) => e.target.style.color = T.text} onMouseLeave={(e) => e.target.style.color = T.textTertiary}>Terms of Use</a>
            <a href="/privacy" style={{ fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textDecoration: "none" }}
              onMouseEnter={(e) => e.target.style.color = T.text} onMouseLeave={(e) => e.target.style.color = T.textTertiary}>Privacy Policy</a>
          </div>
        </div>
      )}

      {/* ── RIGHT PANEL ── */}
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: isMobile ? "flex-start" : "center",
        justifyContent: "center",
        padding: isMobile ? "32px 24px 48px" : "48px 72px",
        background: T.bg,
      }}>
        <div style={{ width: "100%", maxWidth: 480 }}>

          {/* Mobile logo */}
          {isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 36 }}>
              <div style={{ width: 30, height: 30, borderRadius: T.radius, background: "#111827", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <img className="w-4 h-4 brightness-0 invert" src={LOGO} alt="Logo" />
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: FF, letterSpacing: "0.04em" }}>Resolut</span>
            </div>
          )}

          {/* Heading */}
          <div style={{ marginBottom: 36 }}>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: T.text, margin: "0 0 8px", fontFamily: FF, letterSpacing: "-0.02em" }}>
              Initialize session
            </h1>
            <p style={{ fontSize: 14, fontWeight: 500, color: T.textTertiary, margin: 0, fontFamily: FF }}>
              Verify your identity to continue.
            </p>
          </div>

          {/* Invitation banner */}
          {invitationInfo && (
            <div style={{ marginBottom: 24, padding: "12px 16px", background: "#eff6ff", border: "0.5px solid #bfdbfe", borderRadius: T.radius }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#1e40af", fontFamily: FF }}>
                Sign in with <strong>{invitationInfo.email}</strong> to accept your invitation.
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ marginBottom: 24, padding: "11px 14px", background: "#fef2f2", border: "0.5px solid #fecaca", borderRadius: T.radius, display: "flex", alignItems: "center", gap: 10 }}>
              <AlertCircle size={15} color="#dc2626" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 500, color: "#b91c1c", fontFamily: FF }}>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>

            <Field
              label="Email address" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={invitationInfo !== null} required
            />

            {/* Password with show/hide */}
            <div>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.textMuted, fontFamily: FF, marginBottom: 7 }}>
                Password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{
                    width: "100%", height: 42, padding: "0 42px 0 14px",
                    border: "1px solid #d1d5db", borderRadius: T.radius,
                    fontSize: 14, fontWeight: 500, color: T.text,
                    background: T.bg, fontFamily: FF, outline: "none",
                    transition: "border-color 0.12s", boxSizing: "border-box",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = "#111827" }}
                  onBlur={(e)  => { e.target.style.borderColor = "#d1d5db" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", padding: 0,
                    color: T.textTertiary, display: "flex", alignItems: "center",
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div style={{ textAlign: "right", marginTop: 6 }}>
                <Link to="/forgot-password" style={{ fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textDecoration: "underline" }}>
                  Forgot password?
                </Link>
              </div>
            </div>

            <PrimaryBtn loading={loading} type="submit">
              Sign in
            </PrimaryBtn>

            {/* Divider */}
            {!invitationInfo && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ flex: 1, height: "0.5px", background: "#e5e7eb" }} />
                  <span style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary, fontFamily: FF }}>or</span>
                  <div style={{ flex: 1, height: "0.5px", background: "#e5e7eb" }} />
                </div>

                {/* Google */}
                <GhostBtn onClick={handleGoogleSignIn} disabled={googleLoading} style={{ width: "100%", justifyContent: "center" }}>
                  {googleLoading
                    ? <Loader2 size={16} style={{ animation: "login-spin 0.8s linear infinite" }} />
                    : (
                      <svg width="16" height="16" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    )
                  }
                  {googleLoading ? "Connecting…" : "Continue with Google"}
                </GhostBtn>

                {/* SSO */}
                <GhostBtn onClick={() => {}} style={{ width: "100%", justifyContent: "center" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
                  </svg>
                  Continue with SSO
                </GhostBtn>
              </>
            )}

          </form>

          {/* Links */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 24 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textAlign: "center" }}>
              Don't have an account?{" "}
              <Link to="/register" style={{ color: T.text, fontWeight: 600, textDecoration: "underline" }}>Register identity</Link>
            </p>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textAlign: "center" }}>
              Hiring?{" "}
              <Link to="/onboard" style={{ color: T.text, fontWeight: 600, textDecoration: "underline" }}>Create an employer account</Link>
            </p>
          </div>

          {/* Mobile footer */}
          {isMobile && (
            <div style={{ display: "flex", gap: 20, marginTop: 48 }}>
              <a href="/terms" style={{ fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textDecoration: "none" }}>Terms of Use</a>
              <a href="/privacy" style={{ fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: FF, textDecoration: "none" }}>Privacy Policy</a>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default LoginPage